// ui/presenters/modifierRuleAuthoring.js
import { MODIFIER_ROLES, RULE_OUTCOMES } from '../../domain/constants/modifierRule';
import {
  isAuthoredCondition,
  isConditionComplete,
  ruleModifierCodes,
  unreachableRuleRows,
  unstartedRuleRows,
} from '../../domain/services/modifierRules';

/**
 * Which rule rows THIS EDITOR may offer controls on, and which of those it would refuse to save.
 *
 * OUT HERE RATHER THAN IN `domain/`, because the limits below are facts about the screen, not about
 * the model: the stored shape supports several tagged modifiers and an AND of two credentials, and
 * the projector evaluates both. Only this editor cannot show them. A domain that encoded the
 * screen's reach would hand the same refusals to the next writer — a bulk payer-sheet import.
 */

/**
 * Can the editor express the WHOLE of this row?
 *
 * Anything else renders READ-ONLY rather than hidden: hiding loses the row on the next save, and
 * editing a row whose real meaning is invisible rewrites the payer's rule into something else. Every
 * case below is a newer client having written it, since old clients persist.
 */
export function isRuleRowAuthorable(row) {
  // A hold carries no modifier, so it would draw as an unfinished bill row and typing would turn a
  // payer's refusal into a claim.
  if (row?.result?.outcome !== RULE_OUTCOMES.BILL) {
    return false;
  }

  const conditions = row.conditions ?? [];
  const modifiers = row.result.modifiers ?? [];

  if (!Array.isArray(conditions) || !Array.isArray(modifiers)) {
    return false;
  }

  // One control per kind, and `withCondition` replaces every condition of that kind — so editing a
  // row asking two credentials drops one, leaving a rule WIDER than the payer wrote.
  const kinds = new Set(conditions.map((condition) => condition?.fact));

  if (kinds.size !== conditions.length || !conditions.every(isAuthoredCondition)) {
    return false;
  }

  // One modifier field, and `setModifier` replaces the list. A second modifier would be deleted by
  // an edit nobody could see; a lone one tagged anything but `rate` would be retagged, moving it on
  // the line.
  return modifiers.length <= 1
    && modifiers.every((modifier) => modifier?.role === MODIFIER_ROLES.RATE);
} // isRuleRowAuthorable()

/**
 * Is this row finished enough to save?
 *
 * A row with NO conditions is complete: it is the catch-all, and "anyone else on this code bills HM"
 * is a real sheet. What makes that wrong is a row beneath it — `unreachableRuleRows`.
 *
 * A BILL ROW PRODUCING NOTHING IS NOT. "No modifier here" is already said by having no rule.
 */
export function isRuleRowComplete(row) {
  // First, because an unauthorable row renders read-only: faulting it would refuse the save over
  // something nobody can reach a control on. The projector holds such a row, which is where that
  // fault belongs.
  if (!isRuleRowAuthorable(row)) {
    return true;
  }

  if (!row.conditions.every(isConditionComplete)) {
    return false;
  }

  return ruleModifierCodes(row).length > 0;
} // isRuleRowComplete()

/**
 * The positions of rows that cannot be saved as they stand.
 *
 * Unstarted rows are absent — nobody has typed in them and the write drops them. A blank row that
 * SHADOWS one below is not unstarted: the write keeps it, so it has to be resolved rather than
 * silently deleted, and it is editable, so there is a control to resolve it on.
 */
export function incompleteRuleRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  const unstarted = unstartedRuleRows(rows);

  return rows.reduce((positions, row, index) => {
    return unstarted.has(index) || isRuleRowComplete(row) ? positions : positions.concat([index]);
  }, []);
} // incompleteRuleRows()

/**
 * Is this service's rule list in a state nobody should be able to save?
 *
 * TWO FAULTS, ONE ANSWER, because the caller is a Save button. An incomplete row holds every
 * performer it matches while LOOKING configured; an unreachable row is a tier somebody believes they
 * wrote. Both fail quietly at projection time, weeks later.
 */
export function hasRuleProblem(entry) {
  const rows = entry?.modifierRules ?? [];

  return incompleteRuleRows(rows).length > 0 || unreachableRuleRows(rows).length > 0;
} // hasRuleProblem()
